import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { AccountsService } from '../accounts.service';
import { FirebaseStorageService } from '../../services/firebase-storage.service';

export interface AccountingAttachmentView {
    attachmentId?: number;
    fileName?: string;
    fileUrl?: string;
    uploadedAt?: string | Date;
    uploadedBy?: string | number;
}

@Component({
    selector: 'app-accounting-attachments-panel',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, FileUploadModule, InputTextModule, ProgressBarModule, PageDataPanelComponent],
    templateUrl: './accounting-attachments-panel.component.html',
    styleUrls: ['./accounting-attachments-panel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountingAttachmentsPanelComponent implements OnChanges {
    @Input() attachments: AccountingAttachmentView[] = [];
    @Input() documentType?: string;
    @Input() documentId?: number;
    @Input() owningCompanyId?: number;
    @Input() uploadedBy?: number;
    @Input() allowUpload = true;

    attachmentType = 'SUPPORTING_DOCUMENT';
    notes = '';
    uploadProgress = 0;

    constructor(private accountsService: AccountsService, private firebaseStorageService: FirebaseStorageService, private messageService: MessageService, private cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['documentType'] || changes['documentId']) && this.documentType && this.documentId) {
            this.accountsService.getAccountingDocumentAttachments(this.documentType, this.documentId).subscribe({
                next: attachments => {
                    this.attachments = attachments;
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.attachments = [];
                    this.cdr.markForCheck();
                }
            });
        }
    }

    open(fileUrl?: string): void {
        if (fileUrl) {
            window.open(fileUrl, '_blank');
        }
    }

    upload(event: FileSelectEvent): void {
        const file = event.files?.[0];
        if (!file || !this.documentType || !this.documentId) {
            return;
        }
        const safeType = this.documentType.toLowerCase().replace(/[^a-z0-9_-]/g, '-');
        this.firebaseStorageService.uploadFileWithProgress(file, `accounting/${safeType}/${this.documentId}`).subscribe({
            next: upload => {
                this.uploadProgress = upload.progress;
                if (upload.url) {
                    this.accountsService.saveAccountingDocumentAttachment(this.documentType!, this.documentId!, {
                        fileName: file.name,
                        fileUrl: upload.url,
                        contentType: file.type,
                        attachmentType: this.attachmentType,
                        notes: this.notes,
                        owningCompanyId: this.owningCompanyId,
                        uploadedBy: this.uploadedBy
                    }).subscribe({
                        next: attachment => {
                            this.attachments = [...this.attachments, attachment];
                            this.uploadProgress = 0;
                            this.notes = '';
                            this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: `${file.name} attached.` });
                            this.cdr.markForCheck();
                        },
                        error: err => {
                            this.uploadProgress = 0;
                            this.messageService.add({ severity: 'error', summary: 'Attachment failed', detail: err?.error?.message || 'Could not save attachment metadata.' });
                            this.cdr.markForCheck();
                        }
                    });
                }
                this.cdr.markForCheck();
            },
            error: err => {
                this.uploadProgress = 0;
                this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: err?.message || 'Could not upload file.' });
                this.cdr.markForCheck();
            }
        });
    }

    deleteAttachment(attachment: AccountingAttachmentView): void {
        if (!this.documentType || !this.documentId || !attachment.attachmentId) return;
        this.accountsService.deleteAccountingDocumentAttachment(this.documentType, this.documentId, attachment.attachmentId, this.uploadedBy).subscribe({
            next: () => {
                this.attachments = this.attachments.filter(item => item !== attachment);
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Attachment removed.' });
                this.cdr.markForCheck();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Delete failed', detail: err?.error?.message || 'Could not delete attachment.' })
        });
    }
}

